import { FormattedMessage } from "react-intl";

import { StatusPill } from "@ps-design-system/components/StatusPill";
import { Text } from "@ps-design-system/components/Text";
import SignageIcon from "@ps-design-system/icons/Signage/Signage24";

import { useIntl } from "react-intl-compat";

import { TAX_DETAILS_MESSAGES } from "../TaxDetails.messages";
import type { TaxRegistration } from "../TaxDetails.types";
import { formatRegistrationTaxTypeName } from "../TaxDetails.utils";

import { TAX_DETAILS_STATUS_PILL_VARIANTS } from "./TaxDetailsBanner.consts";
import { TAX_DETAILS_STATUS_PILL_MESSAGES } from "./TaxDetailsBanner.messages";

type TaxDetailsBannerHeaderProps = {
  registration: TaxRegistration;
};

/**
 * Renders the registration's heading and, where the status has one, its
 * validation pill.
 *
 * The heading names the tax the registration's own region levies — "VAT
 * details", "HST details" — or, for an empty slot, every tax it covers
 * country-wide: "GST/HST details".
 */
const TaxDetailsBannerHeader = ({
  registration,
}: TaxDetailsBannerHeaderProps) => {
  const intl = useIntl();
  const { status } = registration;

  const pillVariant = TAX_DETAILS_STATUS_PILL_VARIANTS[status];
  const pillMessageDescriptor = TAX_DETAILS_STATUS_PILL_MESSAGES[status];
  const taxTypeNames = formatRegistrationTaxTypeName({ intl, registration });

  return (
    <div className="tw-flex tw-items-center tw-flex-wrap tw-gap-x-16 tw-gap-y-8">
      <div className="tw-flex tw-items-center tw-flex-wrap tw-gap-8">
        <SignageIcon />
        <Text typeface="body-2-m">
          <FormattedMessage
            {...TAX_DETAILS_MESSAGES.registrationHeading}
            values={{ taxTypes: taxTypeNames }}
          />
        </Text>
      </div>

      {pillVariant && pillMessageDescriptor && (
        <StatusPill variant={pillVariant} size="sm">
          <FormattedMessage {...pillMessageDescriptor} />
        </StatusPill>
      )}
    </div>
  );
};

export { TaxDetailsBannerHeader };
